import React, { useState, useEffect } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";

// --- TeksCapital palette ---
const brand = {
  bg: "#000000",
  textSecondary: "#B9BAC0",
  textMuted: "#6F7178",
  accent: "#CDA075",
};

const keyframes = {
  fadeInUp: {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  slide: {
    "0%": { transform: "translateX(-110%)" },
    "100%": { transform: "translateX(260%)" },
  },
};

interface FullScreenLoaderProps {
  messages?: string[];
  /** Si quieres personalizar el título sin tocar el estilo */
  title?: string;
  /** Ruta del logo. Por defecto busca en /public/logo.png */
  logoSrc?: string;
  /** Texto alternativo para accesibilidad */
  logoAlt?: string;
  /** Tamaño del logo (px). Ajusta de forma responsive por defecto */
  logoSizePx?: number;
  /** Mostrar/ocultar logo */
  showLogo?: boolean;
}

const FullScreenLoader: React.FC<FullScreenLoaderProps> = ({
  messages = [
    "Brewing coffee into bytes...",
    "Convincing the server to cooperate...",
    "Sending packets with love...",
    "Aligning bits and bytes...",
    "Consulting the Stack Overflow oracle...",
    "Persuading the mouse to click itself...",
    "Updating internal memes...",
    "Defragmenting space-time...",
    "Checking if it works on my machine...",
    "Hashing blocks with extra salt...",
    "Mining jokes on the testnet...",
    "Waiting for gas fees to drop...",
    "Crossing bridges between chains...",
    "Teaching AI to HODL...",
    "Forking reality...",
    "Debugging smart contracts with duct tape...",
    "Rebasing your patience...",
    "Minting fresh bugs...",
    "Running npm install (pray for me)...",
    "Deploying to production YOLO style...",
    "Trying to stake in real life...",
    "Syncing with the multiverse...",
    "Shilling this loading screen...",
    "Encrypting dad jokes...",
    "Refactoring spaghetti into lasagna...",
    "Launching rockets from the CLI...",
    "Validating blocks of cheese...",
    "Teaching Solidity to my cat...",
    "Resolving merge conflicts with violence...",
    "Escaping from callback hell...",
    "Converting caffeine to code...",
    "Waiting for L2 airdrop...",
    "Rug-pulling test data...",
    "Washing dirty commits...",
    "Benchmarking against chaos...",
    "Streaming vibes to mainnet...",
    "Hot reloading the blockchain...",
    "Deploying memes per second...",
    "Building castles in /tmp...",
    "Unstaking brain cells...",
    "Bribing validators with pizza...",
    "Patching the matrix...",
    "Logging into reality...",
    "Quantum tunneling packets...",
    "Upgrading RAM in my head...",
    "Rate-limiting existential dread...",
    "Ping-ponging nodes into orbit...",
    "Dockerizing my emotions...",
    "Resharding friendships...",
    "Spinning up infinite loops of hope...",
  ],
  title = "Liquidity Monitor",
  logoSrc = "/logo.png",
  logoAlt = "TeksCapital logo",
  logoSizePx,
  showLogo = true,
}) => {
  const theme = useTheme();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(() =>
    Math.floor(Math.random() * messages.length)
  );

  // Rotate the loading messages
  useEffect(() => {
    if (messages.length > 1) {
      const interval = setInterval(() => {
        setCurrentMessageIndex(Math.floor(Math.random() * messages.length));
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [messages.length]);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        "@keyframes fadeInUp": keyframes.fadeInUp,
        "@keyframes slide": keyframes.slide,
        position: "fixed",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 3,
        px: 3,
        bgcolor: brand.bg,
        zIndex: theme.zIndex.drawer + 1,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: { xs: 620, md: 980 },
          height: { xs: 620, md: 820 },
          transform: "translate(-50%, -55%)",
          background: "radial-gradient(closest-side, rgba(205,160,117,0.24), rgba(205,160,117,0) 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        component="img"
        src="/transparentLogo.png"
        alt=""
        aria-hidden
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "min(1100px, 140vw)",
          transform: "translate(-50%, -58%)",
          opacity: 0.35,
          pointerEvents: "none",
          userSelect: "none",
        }}
      />

      <Stack alignItems="center" spacing={2.5} sx={{ position: "relative", animation: "fadeInUp 0.6s ease-out both" }}>
        {showLogo && (
          <Box
            component="img"
            src={logoSrc}
            alt={logoAlt}
            sx={{
              width: logoSizePx ?? { xs: 84, md: 104 },
              height: "auto",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: { xs: "0.75rem", md: "0.8125rem" },
            fontWeight: 600,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: brand.textSecondary,
            textAlign: "center",
            pl: "0.32em",
          }}
        >
          {title}
        </Typography>

        {/* Indeterminate hairline progress */}
        <Box
          sx={{
            width: 168,
            height: 2,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "rgba(255,255,255,0.08)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              width: "40%",
              borderRadius: 2,
              bgcolor: brand.accent,
              animation: "slide 1.4s cubic-bezier(0.65, 0, 0.35, 1) infinite",
            }}
          />
        </Box>
      </Stack>

      <Typography
        key={currentMessageIndex}
        sx={{
          position: "relative",
          textAlign: "center",
          fontSize: { xs: "0.875rem", md: "0.9375rem" },
          maxWidth: 420,
          color: brand.textMuted,
          minHeight: 24,
          animation: "fadeInUp 0.5s ease-out both",
        }}
      >
        {messages[currentMessageIndex]}
      </Typography>
    </Box>
  );
};

export { FullScreenLoader };
